import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { stepsForClient } from '../lib/tourSteps'

const CARD_W = 320
const GAP = 14

// Where the card goes relative to the thing being pointed at. Preference is
// to the right (the anchors are sidebar items), then below, then centred —
// whichever first fits on screen, so the card is never half off the edge.
function placeCard(rect, cardH, vw, vh) {
  if (!rect) return { left: (vw - CARD_W) / 2, top: Math.max(20, (vh - cardH) / 2), arrow: null }

  const clampTop = (t) => Math.min(Math.max(16, t), Math.max(16, vh - cardH - 16))

  if (rect.right + GAP + CARD_W < vw - 12) {
    return { left: rect.right + GAP, top: clampTop(rect.top + rect.height / 2 - cardH / 2), arrow: 'left' }
  }
  if (rect.bottom + GAP + cardH < vh - 12) {
    return { left: Math.min(Math.max(12, rect.left), vw - CARD_W - 12), top: rect.bottom + GAP, arrow: 'up' }
  }
  if (rect.top - GAP - cardH > 12) {
    return { left: Math.min(Math.max(12, rect.left), vw - CARD_W - 12), top: rect.top - GAP - cardH, arrow: 'down' }
  }
  return { left: (vw - CARD_W) / 2, top: clampTop((vh - cardH) / 2), arrow: null }
}

export default function WelcomeTour({ open, content, clientName, onFinish, onSkip, onAskQuestion }) {
  const steps = stepsForClient(content)
  const [i, setI] = useState(0)
  const [rect, setRect] = useState(null)
  const [pos, setPos] = useState({ left: 0, top: 0, arrow: null })
  const cardRef = useRef(null)

  const step = steps[i]
  const last = i === steps.length - 1

  // Restart from the top whenever the tour is reopened from the sidebar.
  useEffect(() => { if (open) setI(0) }, [open])

  // Measure the anchor. A missing anchor is not an error — the step falls back
  // to a centred card rather than pointing at nothing.
  useLayoutEffect(() => {
    if (!open || !step) return

    function measure() {
      const el = document.querySelector(step.anchor)
        || (step.fallbackAnchor ? document.querySelector(step.fallbackAnchor) : null)
      const r = el?.getBoundingClientRect()
      const visible = r && r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight
      setRect(visible ? r : null)
      setPos(placeCard(visible ? r : null, cardRef.current?.offsetHeight || 200, window.innerWidth, window.innerHeight))
    }

    measure()
    // The sidebar scrolls and the window resizes; the ring has to follow.
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, step, i])

  useEffect(() => {
    if (!open) return
    function onKey(e) {
      if (e.key === 'Escape') onSkip?.()
      else if (e.key === 'ArrowRight') setI(v => Math.min(v + 1, steps.length - 1))
      else if (e.key === 'ArrowLeft') setI(v => Math.max(v - 1, 0))
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, steps.length, onSkip])

  if (!open || !step) return null

  const pad = 6
  const first = String(clientName || '').trim().split(/\s+/)[0]

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4000 }} role="dialog" aria-modal="true" aria-label="Portal tour">
      {/* The dim is a huge shadow cast outward from the ring, which leaves the
          anchored element lit without cutting four separate mask panels. */}
      {rect ? (
        <div style={{
          position: 'fixed',
          left: rect.left - pad, top: rect.top - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2,
          borderRadius: 10,
          boxShadow: '0 0 0 9999px rgba(17,43,50,0.62)',
          border: '2px solid var(--red)',
          pointerEvents: 'none',
          transition: 'all 0.22s cubic-bezier(0.4,0,0.2,1)',
        }} />
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,43,50,0.62)' }} onClick={onSkip} />
      )}

      <div
        ref={cardRef}
        style={{
          position: 'fixed', left: pos.left, top: pos.top, width: CARD_W,
          background: 'var(--card)', borderRadius: 12,
          boxShadow: '0 12px 40px rgba(17,43,50,0.28)',
          padding: '20px 22px 18px',
          transition: 'left 0.22s cubic-bezier(0.4,0,0.2,1), top 0.22s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {i === 0 && (
          <div style={{ fontFamily: 'var(--fu)', fontSize: 12.5, color: 'var(--neutral-mid)', marginBottom: 10 }}>
            {first ? `Welcome, ${first}.` : 'Welcome.'} Here's the one-minute version.
          </div>
        )}

        <div style={{ fontFamily: 'var(--fu)', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--red)', marginBottom: 8 }}>
          Step {i + 1} of {steps.length}
        </div>

        <div style={{ fontFamily: 'var(--fd)', fontSize: 17, fontWeight: 700, color: 'var(--navy)', marginBottom: 7, letterSpacing: '-0.01em' }}>
          {step.title}
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.62, color: 'var(--neutral-mid)', marginBottom: 18 }}>
          {step.body}
        </div>

        {onAskQuestion && (
          <div style={{ borderTop: '1px solid var(--neutral-border)', paddingTop: 12, marginBottom: 13 }}>
            <button
              onClick={onAskQuestion}
              style={{ background: 'none', border: 'none', padding: 0, fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--red)', cursor: 'pointer', textAlign: 'left' }}
            >
              Rather just ask? Message me directly →
            </button>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', gap: 5, flex: 1 }}>
            {steps.map((s, n) => (
              <div key={s.key} style={{
                width: n === i ? 16 : 6, height: 6, borderRadius: 3,
                background: n === i ? 'var(--red)' : 'var(--neutral-border)',
                transition: 'all 0.2s',
              }} />
            ))}
          </div>

          <button
            onClick={onSkip}
            style={{ background: 'none', border: 'none', padding: '7px 4px', fontFamily: 'var(--fu)', fontSize: 12.5, color: 'var(--neutral-mid)', cursor: 'pointer' }}
          >
            Skip
          </button>

          {i > 0 && (
            <button
              onClick={() => setI(v => v - 1)}
              style={{ background: 'none', border: '1px solid var(--neutral-border)', borderRadius: 7, padding: '7px 12px', fontFamily: 'var(--fu)', fontSize: 12.5, color: 'var(--navy)', cursor: 'pointer' }}
            >
              Back
            </button>
          )}

          <button
            onClick={() => (last ? onFinish?.() : setI(v => v + 1))}
            style={{ background: 'var(--accent-soft)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', borderRadius: 7, padding: '8px 15px', fontFamily: 'var(--fu)', fontSize: 12.5, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer' }}
          >
            {last ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}
